import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Download, Calendar, ExternalLink } from 'lucide-react'
import { projects } from '@/data/projects'
import { SEO } from '@/components/SEO'
import { GlassCard } from '@/components/ui/glass-card'
import { useLuxeHero, useSectionSequence } from '@/hooks'
import { usePageView, useAnalytics } from '@/lib/analytics'
import { useUTMAttribution } from '@/hooks/useUTMAttribution'

const CALENDLY_URL = 'https://calendly.com/garrett-garrettjohnlaw/15min'
const LANDINGS_URL = '/for-data/landings.json'

// ---------------------------------------------------------------------------
// Static landings index — same-origin JSON served via CloudFront. Populated
// by projects/personal/gjl-software/scripts/generate-resumes.ts. No runtime
// dependency on Finboard API.
// ---------------------------------------------------------------------------

interface LandingEntry {
  slug: string
  company: string
  role: string
  url?: string
  level?: string
  locations?: string[]
  remotePolicy?: string
  baseMin?: number | null
  baseMax?: number | null
  compNotes?: string
  tailoredHeadline: string
  tailoredLead?: string
  tailoredBullets: string[]
  portfolioOrder: string[]
  landingPageBlurb?: string
  pdfUrl?: string
  updatedAt: string
}

interface LandingsIndex {
  generatedAt: string
  landings: Record<string, LandingEntry>
}

// Single-flight cache so multiple landing pages in one session only fetch once.
let landingsPromise: Promise<LandingsIndex> | null = null

function fetchLandings(): Promise<LandingsIndex> {
  if (!landingsPromise) {
    landingsPromise = fetch(LANDINGS_URL, { cache: 'no-cache' }).then((r) => {
      if (!r.ok) throw new Error(`landings.json ${r.status}`)
      return r.json() as Promise<LandingsIndex>
    })
  }
  return landingsPromise
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CompanyLanding() {
  const { companyId = '' } = useParams<{ companyId: string }>()

  useUTMAttribution()
  usePageView('Company Landing', { companyId })
  const analytics = useAnalytics()

  const [data, setData] = useState<LandingEntry | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'not-found' | 'error'>('loading')

  useEffect(() => {
    let cancelled = false
    fetchLandings()
      .then((idx) => {
        if (cancelled) return
        const entry = idx.landings[companyId]
        if (!entry) {
          setState('not-found')
          return
        }
        setData(entry)
        setState('ready')
      })
      .catch(() => {
        if (cancelled) return
        setState('error')
      })
    return () => {
      cancelled = true
    }
  }, [companyId])

  const heroRef = useLuxeHero<HTMLElement>(0.3)
  const highlightsRef = useSectionSequence<HTMLElement>([
    { selector: 'h2', direction: 'left', delay: 0 },
    { selector: '.highlight-card', direction: 'bottom', stagger: 0.08, delay: 0.15 },
  ])
  const projectsRef = useSectionSequence<HTMLElement>([
    { selector: 'h2', direction: 'right', delay: 0 },
    { selector: '.project-card', direction: 'bottom', stagger: 0.1, delay: 0.15 },
  ])

  // --- Non-ready states ---------------------------------------------------

  if (state === 'loading') {
    return (
      <section className="relative px-4 py-32">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-muted-foreground font-mono">Loading…</p>
        </div>
      </section>
    )
  }

  if (state === 'not-found' || state === 'error' || !data) {
    return (
      <>
        <SEO title="Not found" description="No tailored page for this company." />
        <section className="relative px-4 py-32">
          <div className="mx-auto max-w-3xl space-y-4 text-center">
            <p className="text-primary font-mono text-sm">&lt;404 /&gt;</p>
            <h1 className="text-fluid-h1 text-foreground">No tailored page yet</h1>
            <p className="text-muted-foreground mx-auto max-w-xl">
              Looking for a specific company? The page at{' '}
              <code className="text-primary font-mono">/for/{companyId}</code> hasn't been built yet. Reach out directly
              —{' '}
              <a href="mailto:garrett@garrettjohnlaw.com" className="text-primary underline">
                garrett@garrettjohnlaw.com
              </a>
              .
            </p>
          </div>
        </section>
      </>
    )
  }

  const {
    company,
    role,
    tailoredHeadline,
    tailoredLead,
    tailoredBullets,
    portfolioOrder,
    landingPageBlurb,
    pdfUrl,
    baseMin,
    baseMax,
    remotePolicy,
  } = data

  const featuredProjects = (portfolioOrder ?? [])
    .map((slug) => projects.find((p) => p.slug === slug))
    .filter((p): p is NonNullable<typeof p> => Boolean(p))

  const handleResumeClick = () => {
    analytics.track('resume_download', {
      companyId,
      slug: data.slug,
      pdfUrl,
    })
  }

  const handleCalendlyClick = () => {
    analytics.track('calendly_click', { companyId, slug: data.slug })
  }

  return (
    <>
      <SEO
        title={`For ${company}`}
        description={
          tailoredHeadline ??
          `Tailored positioning for ${company} — Garrett Law, senior software engineer + real estate operator.`
        }
        url={`https://software.garrettjohnlaw.com/for/${companyId}`}
      />

      {/* Hero */}
      <section ref={heroRef} className="relative overflow-hidden px-4 py-20 md:py-28">
        <div className="bg-gradient-radial-cyber absolute inset-0 opacity-30" />
        <div className="relative z-10 mx-auto max-w-4xl">
          <p className="text-primary mb-4 font-mono text-sm tracking-wide">&lt;for.{companyId} /&gt;</p>
          <h1 className="text-fluid-h1 text-foreground mb-6">{tailoredHeadline ?? `Why I'm writing to ${company}`}</h1>
          {tailoredLead && (
            <p className="text-fluid-body-lg text-muted-foreground max-w-3xl whitespace-pre-line">{tailoredLead}</p>
          )}

          <div className="mt-8 flex flex-wrap gap-4">
            {pdfUrl && (
              <a
                href={pdfUrl}
                onClick={handleResumeClick}
                target="_blank"
                rel="noopener noreferrer"
                className="gradient-cyber text-primary-foreground inline-flex items-center gap-2 rounded-lg px-5 py-3 font-medium transition-opacity hover:opacity-95"
              >
                <Download className="h-4 w-4" />
                Download résumé (PDF)
              </a>
            )}
            <a
              href={CALENDLY_URL}
              onClick={handleCalendlyClick}
              target="_blank"
              rel="noopener noreferrer"
              className="border-primary/40 text-foreground hover:border-primary inline-flex items-center gap-2 rounded-lg border px-5 py-3 transition-colors"
            >
              <Calendar className="h-4 w-4" />
              Book a 15-minute call
            </a>
          </div>

          {role && (
            <p className="text-muted-foreground mt-6 font-mono text-xs">
              Re: {role}
              {baseMin && baseMax ? `  ·  $${Math.round(baseMin / 1000)}K–$${Math.round(baseMax / 1000)}K` : ''}
              {remotePolicy ? `  ·  ${remotePolicy}` : ''}
            </p>
          )}
        </div>
      </section>

      <div className="section-divider mx-auto max-w-4xl" />

      {landingPageBlurb && (
        <>
          <section className="px-4 py-16 md:py-20">
            <div className="mx-auto max-w-3xl">
              <p className="text-fluid-body-lg text-foreground leading-relaxed whitespace-pre-line">
                {landingPageBlurb}
              </p>
            </div>
          </section>
          <div className="section-divider mx-auto max-w-4xl" />
        </>
      )}

      {tailoredBullets && tailoredBullets.length > 0 && (
        <>
          <section ref={highlightsRef} className="px-4 py-16 md:py-24">
            <div className="mx-auto max-w-4xl">
              <h2 className="text-fluid-h2 text-foreground mb-8">Highlights relevant to this role</h2>
              <div className="grid gap-4 md:grid-cols-2">
                {tailoredBullets.map((bullet, i) => (
                  <GlassCard key={i} className="highlight-card">
                    <p className="text-foreground leading-relaxed">{bullet}</p>
                  </GlassCard>
                ))}
              </div>
            </div>
          </section>
          <div className="section-divider mx-auto max-w-4xl" />
        </>
      )}

      {featuredProjects.length > 0 && (
        <>
          <section ref={projectsRef} className="px-4 py-16 md:py-24">
            <div className="mx-auto max-w-5xl">
              <h2 className="text-fluid-h2 text-foreground mb-8">Selected projects</h2>
              <div className="grid gap-6">
                {featuredProjects.map((project) => (
                  <GlassCard key={project.slug} hover className="project-card">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <h3 className="text-foreground text-xl font-semibold">
                          {project.url ? (
                            <a
                              href={project.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-primary inline-flex items-center gap-2 transition-colors"
                            >
                              {project.title}
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          ) : (
                            project.title
                          )}
                        </h3>
                      </div>
                      <p className="text-muted-foreground">{project.description}</p>
                      {project.tech && <p className="text-primary font-mono text-xs">{project.tech.join(' · ')}</p>}
                    </div>
                  </GlassCard>
                ))}
              </div>
            </div>
          </section>
          <div className="section-divider mx-auto max-w-4xl" />
        </>
      )}

      <section className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-3xl space-y-6 text-center">
          <h2 className="text-fluid-h2 text-foreground">Let's talk</h2>
          <p className="text-muted-foreground">
            Email me at{' '}
            <a href="mailto:garrett@garrettjohnlaw.com" className="text-primary underline">
              garrett@garrettjohnlaw.com
            </a>{' '}
            or grab time directly.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {pdfUrl && (
              <a
                href={pdfUrl}
                onClick={handleResumeClick}
                target="_blank"
                rel="noopener noreferrer"
                className="gradient-cyber text-primary-foreground inline-flex items-center gap-2 rounded-lg px-5 py-3 font-medium transition-opacity hover:opacity-95"
              >
                <Download className="h-4 w-4" />
                Download résumé
              </a>
            )}
            <a
              href={CALENDLY_URL}
              onClick={handleCalendlyClick}
              target="_blank"
              rel="noopener noreferrer"
              className="border-primary/40 text-foreground hover:border-primary inline-flex items-center gap-2 rounded-lg border px-5 py-3 transition-colors"
            >
              <Calendar className="h-4 w-4" />
              Book 15 min
            </a>
          </div>
        </div>
      </section>
    </>
  )
}
